import { useState, useEffect } from "react";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../../../util/firebase";
import { query, collection, getDocs, where, addDoc } from "firebase/firestore";

import { v4 as uuidv4 } from "uuid";

import { useNavigate } from "react-router-dom";

import BarLoader from "react-spinners/BarLoader";

import { FaChevronLeft } from "react-icons/fa";
import { Button, Input } from "reactstrap";

import emailjs from "@emailjs/browser";

import { FREE_TO_PLAY } from "../../../../config/appMode";

// Returns the current local time formatted for a <input type="datetime-local">
// value, i.e. "YYYY-MM-DDTHH:mm".
function nowLocalInput() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AdminCreateRoomPage() {
  const [user] = useAuthState(auth);

  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // eslint-disable-next-line
  const [userData, setUserData] = useState();
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const doc = await getDocs(q);
      const data = doc.docs[0].data();
      setUserData(data);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      alert("An error occured while fetching user data");
    }
  };
  useEffect(() => {
    if (user) {
      fetchUserData();
    }
    // eslint-disable-next-line
  }, [user]);

  const [leagues, setLeagues] = useState([]);
  const fetchLeagues = async () => {
    try {
      setIsLoading(true);

      const q = query(collection(db, "leagues"));
      const querySnapshot = await getDocs(q);
      let fetchedLeagues = [];
      querySnapshot.forEach((doc) => {
        fetchedLeagues.push(doc.data());
      });
      setLeagues(fetchedLeagues);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      alert("An error occured while fetching leagues");
    }
  };

  useEffect(() => {
    fetchLeagues();
    // eslint-disable-next-line
  }, []);

  const [league, setLeague] = useState("");
  const [season, setSeason] = useState("");
  // In free-to-play mode default the opening to "now" so a freshly created
  // tournament is immediately open and joinable (admin can still change it).
  const [openingDateTime, setOpeningDateTime] = useState(() =>
    FREE_TO_PLAY ? nowLocalInput() : undefined
  );
  const [closingDateTime, setClosingDateTime] = useState();
  const [durationType, setDurationType] = useState("");
  // Tournament types are auto-created as one room per type. In free-to-play
  // mode there is a single tournament (no types), so we create just one room.
  const tournamentTypes = FREE_TO_PLAY
    ? ["casual"]
    : ["casual", "standard", "highStakes", "vipExclusive"];
  // const [status, setStatus] = useState('');

  const createRoom = async () => {
    try {
      setIsLoading(true);

      const leagueName = leagues.find((l) => l.uid === league).name;

      // create a room per tournament type
      const createPromises = tournamentTypes.map((tt) => {
        return addDoc(collection(db, "rooms"), {
          uid: uuidv4(),
          isActive: true,
          season: season,
          memberUids: [],
          members: [],
          submissions: [],
          leagueLogo: leagues.find((l) => l.uid === league).image,
          leagueName: leagueName,
          leagueOrigin: leagues.find((l) => l.uid === league).origin,
          leagueSport: leagues.find((l) => l.uid === league).sportCategory,
          leagueUid: league,
          createdAt: new Date().toISOString(),
          openingDateTime: openingDateTime,
          closingDateTime: closingDateTime,
          durationType: durationType,
          tournamentType: tt,
        });
      });

      await Promise.all(createPromises);

      let users = [];

      // Send in-app notification
      const q = query(collection(db, "users"));
      const docs = await getDocs(q);
      await docs.forEach(async (doc) => {
        const newUser = doc.data();

        users.push(newUser);

        const newInAppNotification = {
          uid: uuidv4(),
          title: "New season",
          description: `${leagueName}'s ${season} season is now live!`,
          recipientUid: newUser.uid,
          dateCreated: new Date().toISOString(),
          isRead: false,
        };
        await addDoc(collection(db, "notifications"), newInAppNotification);
      });
      //

      // Send email notification (localized template variables)
      const serviceId = "sportleague_outlook";
      const templateId = "newSeason";
      const publicKey = "Ki5BETKiWrS6RDEP_";

      // Build list of recipients (exclude users without email)
      const recipients = users.filter((u) => u && u.email);

      // Translation map for the new season email (per-recipient localization)
      const emailTranslations = {
        en: {
          subject: "A new season is out: {leagueName} — {season}",
          greeting: "Hi",
          paragraphIntro:
            "Heads up, <strong>{leagueName}</strong>'s <strong>{season}</strong> season is now live!",
          paragraphAction:
            "Click the button below to view the league and join the new season.",
          buttonText: "GO TO SPORTLEAGUE",
          fallbackIntro:
            "If the button above doesn't work, copy and paste this link into your browser:",
          footerNotice:
            "You are receiving this email because you're a member of {leagueName}.",
          copyright: "© 2025 Sportleague. All rights reserved.",
        },
        es: {
          subject: "¡Nueva temporada: {leagueName} — {season}!",
          greeting: "Hola",
          paragraphIntro:
            "Atención, la temporada <strong>{season}</strong> de <strong>{leagueName}</strong> ya está activa!",
          paragraphAction:
            "Haz clic en el botón para ver la liga y unirte a la nueva temporada.",
          buttonText: "IR A SPORTLEAGUE",
          fallbackIntro:
            "Si el botón anterior no funciona, copia y pega este enlace en tu navegador:",
          footerNotice:
            "Recibes este correo porque eres miembro de {leagueName}.",
          copyright: "© 2025 Sportleague. Todos los derechos reservados.",
        },
      };

      // Prepare a mapping of uid -> language for recipients missing a language value
      const uidsNeedingLookup = recipients
        .filter((r) => !r.language || !r.language?.value)
        .map((r) => r.uid)
        .filter(Boolean);

      const uidToLang = {};
      const chunkSize = 10; // Firestore 'in' supports up to 10
      for (let i = 0; i < uidsNeedingLookup.length; i += chunkSize) {
        const chunk = uidsNeedingLookup.slice(i, i + chunkSize);
        try {
          const qLang = query(
            collection(db, "users"),
            where("uid", "in", chunk)
          );
          const docsLang = await getDocs(qLang);
          docsLang.forEach((docSnap) => {
            const data = docSnap.data();
            if (data?.uid) {
              uidToLang[data.uid] = data?.language?.value || "en";
            }
          });
        } catch (err) {
          console.error("Error fetching recipient languages", err);
        }
      }

      // Send emails sequentially per recipient to respect rate limits and provide per-recipient localization
      for (const recipient of recipients) {
        const langFromRecipient = recipient?.language?.value;
        const lang = langFromRecipient || uidToLang[recipient.uid] || "en";
        const t = emailTranslations[lang] || emailTranslations["en"];

        const subject = (t.subject || "")
          .replace("{leagueName}", leagueName)
          .replace("{season}", season);

        const paragraphIntro = (t.paragraphIntro || "")
          .replace("{leagueName}", leagueName)
          .replace("{season}", season);

        const paragraphAction = t.paragraphAction || "";
        const fallbackIntro = t.fallbackIntro || "";
        const footerNotice = (t.footerNotice || "").replace(
          "{leagueName}",
          leagueName
        );
        const copyright = t.copyright || "";

        const templateParams = {
          toEmail: recipient.email,
          toUsername: recipient.username || recipient.name || "",
          leagueName: leagueName,
          leagueUid: league,
          season: season,
          linkToLeague: `https://app.sportleague.com/play/leagues/${league}`,
          subject,
          greeting: t.greeting,
          paragraphIntro,
          paragraphAction,
          buttonText: t.buttonText,
          fallbackIntro,
          footerNotice,
          copyright,
        };

        try {
          const resp = await emailjs.send(
            serviceId,
            templateId,
            templateParams,
            publicKey
          );
          console.log(
            "EmailJS SUCCESS",
            recipient.email,
            resp.status,
            resp.text
          );
        } catch (err) {
          console.error("EmailJS FAILED for", recipient.email, err);
        }
      }
      //

      navigate(-1);

      setIsLoading(false);
    } catch (err) {
      alert(err.message);
      setIsLoading(false);
    }
  };

  if (isLoading)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{
            width: 100,
            marginBottom: "1rem",
          }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="newsArticlePage" className="page">
      <h1>
        <FaChevronLeft
          onClick={() => navigate(-1)}
          style={{
            color: "#005EE1",
            width: 35,
            height: 35,
            marginTop: -8,
            marginRight: "0.25rem",
            cursor: "pointer",
          }}
        />
        CREATE TOURNAMENT
      </h1>
      <div>
        <p className="inputLabel" style={{ textAlign: "left" }}>
          League
        </p>
        <Input
          type="select"
          className="mainInput"
          value={league}
          onChange={(e) => setLeague(e.target.value)}
          defaultValue={""}
          style={{ marginBottom: "0.5rem" }}
        >
          <option value="" disabled>
            The tournament's league
          </option>
          {leagues.map((league, index) => (
            <option key={index} value={league.uid}>
              {league.name}
            </option>
          ))}
        </Input>
        <p className="inputLabel" style={{ textAlign: "left" }}>
          Name
        </p>
        <input
          type="text"
          className="mainInput"
          value={season}
          onChange={(e) => setSeason(e.target.value)}
          placeholder="The tournament's name"
          style={{ marginBottom: "0.5rem" }}
        />
        <p className="inputLabel" style={{ textAlign: "left" }}>
          Opening date & time
        </p>
        <Input
          type="datetime-local"
          className="mainInput"
          value={openingDateTime}
          onChange={(e) => setOpeningDateTime(e.target.value)}
          style={{ marginBottom: "0.5rem" }}
        />
        <p className="inputLabel" style={{ textAlign: "left" }}>
          Closing date & time
        </p>
        <Input
          type="datetime-local"
          className="mainInput"
          value={closingDateTime}
          onChange={(e) => setClosingDateTime(e.target.value)}
          style={{ marginBottom: "0.5rem" }}
        />
        <p className="inputLabel" style={{ textAlign: "left" }}>
          Duration type
        </p>
        <Input
          type="select"
          className="mainInput"
          value={durationType}
          onChange={(e) => setDurationType(e.target.value)}
          defaultValue={""}
          style={{ marginBottom: "0.5rem" }}
        >
          <option value="" disabled>
            The tournament's duration type
          </option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </Input>
        {/* 'Tournament type' field removed */}
        {/* <p className='inputLabel' style={{textAlign: 'left'}}>
					Status
        </p>
        <Input
          type="select"
          className="mainInput"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          defaultValue={''}
          style={{marginBottom: '0.5rem'}}
        >
          <option value='' disabled>The room's status</option>
          <option value='active'>Active</option>
          <option value='inactive'>Inactive</option>
        </Input> */}
        <Button
          onClick={createRoom}
          className="primaryBtn"
          style={{ marginTop: "1rem" }}
          disabled={!league || !season || !openingDateTime || !closingDateTime}
        >
          CREATE TOURNAMENT
        </Button>
      </div>
    </div>
  );
}

export default AdminCreateRoomPage;
